'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import PlatformSelector from './PlatformSelector';
import ContentTypeSelector from './ContentTypeSelector';
import NicheSelector from './NicheSelector';
import RateBreakdownPanel from './RateBreakdownPanel';
import { calculateRate } from '@/lib/rateCalculator';
import type { Platform, ContentType, Niche, RateInput, RateBreakdown } from '@/lib/rateCalculator';
import { Users, Activity, Shield, Lock, Info, Zap } from 'lucide-react';
import { savePastCalculation } from './PastCalculationsSidebar';
import { useLanguage } from '@/context/LanguageContext';

interface FormValues {
  platform: Platform;
  followers: string;
  engagementRate: string;
  contentType: ContentType;
  niche: Niche;
  usageRights: boolean;
  exclusivity: boolean;
}

interface RateCalculatorFormProps {
  onCalculated?: () => void;
  restoreInput?: RateInput | null;
}

export default function RateCalculatorForm({ onCalculated, restoreInput }: RateCalculatorFormProps) {
  const [breakdown, setBreakdown] = useState<RateBreakdown | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [currentInput, setCurrentInput] = useState<RateInput>({
    platform: 'instagram',
    followers: 0,
    engagementRate: 0,
    contentType: 'reel',
    niche: 'lifestyle',
    usageRights: false,
    exclusivity: false,
  });
  const { t } = useLanguage();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      platform: 'instagram',
      followers: '',
      engagementRate: '',
      contentType: 'reel',
      niche: 'lifestyle',
      usageRights: false,
      exclusivity: false,
    },
  });

  // Restore form values when restoreInput changes
  React.useEffect(() => {
    if (restoreInput) {
      reset({
        platform: restoreInput.platform,
        followers: String(restoreInput.followers),
        engagementRate: String(restoreInput.engagementRate),
        contentType: restoreInput.contentType,
        niche: restoreInput.niche,
        usageRights: restoreInput.usageRights,
        exclusivity: restoreInput.exclusivity,
      });
      toast.success(t.form.restoredSuccess);
    }
  }, [restoreInput, reset, t.form.restoredSuccess]);

  const watchedPlatform = watch('platform');
  const watchedContentType = watch('contentType');
  const watchedNiche = watch('niche');
  const watchedUsageRights = watch('usageRights');
  const watchedExclusivity = watch('exclusivity');

  const onSubmit = useCallback(
    (data: FormValues) => {
      const followers = parseInt(data.followers.replace(/,/g, ''), 10);
      const engagementRate = parseFloat(data.engagementRate);

      if (isNaN(followers) || followers <= 0) {
        toast.error(t.form.invalidFollowers);
        return;
      }
      if (isNaN(engagementRate) || engagementRate <= 0 || engagementRate > 100) {
        toast.error(t.form.invalidEngagement);
        return;
      }

      setIsCalculating(true);
      const input: RateInput = {
        platform: data.platform,
        followers,
        engagementRate,
        contentType: data.contentType,
        niche: data.niche,
        usageRights: data.usageRights,
        exclusivity: data.exclusivity,
      };

      setCurrentInput(input);

      setTimeout(() => {
        const result = calculateRate(input);
        setBreakdown(result);
        setIsCalculating(false);

        // Save to localStorage for rate card page
        localStorage.setItem('vv18_rate_input', JSON.stringify(input));
        localStorage.setItem('vv18_rate_breakdown', JSON.stringify(result));

        // Save to past calculations history
        savePastCalculation(input, result);
        onCalculated?.();
      }, 400);
    },
    [onCalculated, t]
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 xl:gap-8 w-full">
      {/* Left: Form */}
      <div className="card-elevated p-6 xl:p-8 flex flex-col gap-6">
        <div>
          <h2 className="text-xl font-bold text-foreground">{t.form.title}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t.form.subtitle}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
          {/* Platform */}
          <Controller
            name="platform"
            control={control}
            render={({ field }) => (
              <PlatformSelector
                selected={field.value}
                onChange={(p) => {
                  field.onChange(p);
                  const types: Record<Platform, ContentType> = {
                    instagram: 'reel',
                    tiktok: 'reel',
                    youtube: 'video',
                  };
                  setValue('contentType', types[p]);
                }}
              />
            )}
          />

          {/* Followers */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5 tracking-wide" htmlFor="followers">
              <span className="flex items-center gap-1.5">
                <Users size={13} className="text-primary" />
                {t.form.followers}
              </span>
            </label>
            <p className="text-xs text-muted-foreground mb-2">{t.form.followersHint}</p>
            <input
              id="followers"
              type="number"
              min="1"
              placeholder={t.form.followersPlaceholder}
              className="input-field w-full px-4 py-3 text-sm"
              {...register('followers', {
                required: t.form.followersRequired,
                min: { value: 1, message: t.form.followersMin },
              })}
            />
            {errors.followers && (
              <p className="text-xs text-danger mt-1.5">{errors.followers.message}</p>
            )}
          </div>

          {/* Engagement Rate */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5 tracking-wide" htmlFor="engagementRate">
              <span className="flex items-center gap-1.5">
                <Activity size={13} className="text-primary" />
                {t.form.engagement}
              </span>
            </label>
            <p className="text-xs text-muted-foreground mb-2">{t.form.engagementHint}</p>
            <div className="relative">
              <input
                id="engagementRate"
                type="number"
                step="0.1"
                min="0.1"
                max="100"
                placeholder={t.form.engagementPlaceholder}
                className="input-field w-full px-4 py-3 text-sm pr-10"
                {...register('engagementRate', {
                  required: t.form.engagementRequired,
                  min: { value: 0.1, message: t.form.engagementMin },
                  max: { value: 100, message: t.form.engagementMax },
                })}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
                %
              </span>
            </div>
            {errors.engagementRate && (
              <p className="text-xs text-danger mt-1.5">{errors.engagementRate.message}</p>
            )}
          </div>

          {/* Content Type */}
          <Controller
            name="contentType"
            control={control}
            render={({ field }) => (
              <ContentTypeSelector
                platform={watchedPlatform}
                selected={field.value}
                onChange={field.onChange}
              />
            )}
          />

          {/* Niche */}
          <Controller
            name="niche"
            control={control}
            render={({ field }) => (
              <NicheSelector selected={field.value} onChange={field.onChange} />
            )}
          />

          {/* Add-ons */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-3 tracking-wide">
              {t.form.addons}
              <span className="ml-2 text-xs text-muted-foreground font-normal">
                {t.form.addonsHint}
              </span>
            </label>
            <div className="flex flex-col gap-2">
              {/* Usage Rights */}
              <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/20 hover:bg-muted/30 transition-colors">
                <div className="flex items-start gap-2.5">
                  <Shield size={15} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-foreground">{t.form.usageRights}</div>
                    <div className="text-xs text-muted-foreground">{t.form.usageRightsDesc}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-positive">+25%</span>
                  <Controller
                    name="usageRights"
                    control={control}
                    render={({ field }) => (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={field.value}
                        onClick={() => field.onChange(!field.value)}
                        className={`toggle-track ${field.value ? 'on' : ''}`}
                      >
                        <div className="toggle-thumb" />
                      </button>
                    )}
                  />
                </div>
              </div>

              {/* Exclusivity */}
              <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/20 hover:bg-muted/30 transition-colors">
                <div className="flex items-start gap-2.5">
                  <Lock size={15} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-foreground">{t.form.exclusivity}</div>
                    <div className="text-xs text-muted-foreground">{t.form.exclusivityDesc}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-positive">+30%</span>
                  <Controller
                    name="exclusivity"
                    control={control}
                    render={({ field }) => (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={field.value}
                        onClick={() => field.onChange(!field.value)}
                        className={`toggle-track ${field.value ? 'on' : ''}`}
                      >
                        <div className="toggle-thumb" />
                      </button>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-info/5 border border-info/20">
            <Info size={13} className="text-info flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-muted-foreground leading-relaxed">{t.form.disclaimer}</p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isCalculating}
            className="btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2"
          >
            {isCalculating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t.form.calculating}
              </>
            ) : (
              <>
                <Zap size={16} />
                {t.form.calculate}
              </>
            )}
          </button>
        </form>
      </div>

      {/* Right: Rate Breakdown */}
      <div className="lg:sticky lg:top-24 lg:self-start">
        <RateBreakdownPanel
          breakdown={breakdown}
          input={currentInput}
          isCalculating={isCalculating}
        />
      </div>
    </div>
  );
}